/**
 * A tab still running the build before a deploy (2026-10-14).
 *
 * Vite names every chunk by its content hash, and a deploy replaces the whole `/assets` folder, so a tab
 * opened before the deploy that later imports a chunk on demand (the browser voice client, for one) asks
 * for a file that no longer exists: nginx answers 404 and the import rejects. The page cannot recover
 * by itself; loading the page again fetches the current `index.html` and the current chunks.
 *
 * It reloads ONCE: a second failure within {@link STALE_BUILD_RELOAD_WINDOW_MS} is not a stale build
 * (the chunk is missing from the current build too, or the network is down), and reloading again would
 * loop. That failure is shown as the page's error instead, with Try again.
 *
 * The mark is kept in `sessionStorage` (this tab only). Storage that cannot be read or written (a
 * private window, blocked site data) means no mark can be kept, so the page does not reload at all —
 * a visible error rather than a possible loop.
 */

/** The `sessionStorage` key holding when this tab last reloaded for a stale build (epoch milliseconds). */
export const STALE_BUILD_RELOAD_KEY = "wetalk.console.stale-build-reload-at";

/** A second stale-build failure this soon after the reload is not reloaded again. */
export const STALE_BUILD_RELOAD_WINDOW_MS = 60_000;

/** What the browsers say when an on-demand import or its preload cannot be fetched. */
const STALE_BUILD_MESSAGE: readonly RegExp[] = [
  /Failed to fetch dynamically imported module/i, // Chromium (Chrome, Edge)
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite's own preload of a chunk's stylesheet
];

/** Whether `error` is a chunk of this build that could not be fetched. */
export function isStaleBuildError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return STALE_BUILD_MESSAGE.some((pattern) => pattern.test(error.message));
}

export interface StaleBuildReload {
  /** This tab's session storage, or `null` when the browser refuses it. */
  readonly storage: Pick<Storage, "getItem" | "setItem"> | null;
  readonly now: number;
  readonly reload: () => void;
}

/** Reloads the page unless it already did so within the window; says whether it reloaded. */
export function reloadOnceForStaleBuild({ storage, now, reload }: StaleBuildReload): boolean {
  if (storage === null) return false;
  try {
    const previous = Number(storage.getItem(STALE_BUILD_RELOAD_KEY));
    if (Number.isFinite(previous) && previous > 0 && now - previous < STALE_BUILD_RELOAD_WINDOW_MS) return false;
    storage.setItem(STALE_BUILD_RELOAD_KEY, String(now));
  } catch {
    // Storage that throws keeps no mark, and a reload without a mark could loop.
    return false;
  }
  reload();
  return true;
}

/** The browser's own seams. */
export function browserStaleBuildReload(): StaleBuildReload {
  let storage: Pick<Storage, "getItem" | "setItem"> | null;
  try {
    storage = globalThis.sessionStorage;
  } catch {
    storage = null;
  }
  return {
    storage,
    now: Date.now(),
    reload: () => {
      globalThis.location.reload();
    },
  };
}
